import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@clerk/clerk-react";
import ReactMarkdown from "react-markdown";
import { useChatbot } from "../../contexts/chatbot-context";
import { useSidebar } from "../../contexts/sidebar-context";
import config from "../../config";
import s from "./styles.module.scss";
import clearLogo from "../../assets/clearlogo.svg";

// Cache keys - must match calendar component
const CACHE_KEY = 'ocean_calendar_events';
const CACHE_TIMESTAMP_KEY = 'ocean_calendar_timestamp';

/**
 * Update localStorage cache based on mutations from chatbot.
 * This keeps the calendar in sync without requiring a full page refresh.
 */
const updateLocalStorageCache = (mutations) => {
  if (!mutations || mutations.length === 0) return;

  const cachedEvents = localStorage.getItem(CACHE_KEY);
  if (!cachedEvents) {
    console.log('[Chatbot] No cache exists, skipping update');
    return; // No cache to update
  }
  console.log('[Chatbot] Updating cache with mutations');

  let events = JSON.parse(cachedEvents);
  let cacheModified = false;

  mutations.forEach(mutation => {
    if (mutation.type === 'delete') {
      // Remove deleted event from cache
      const deleteId = `stored_${mutation.event_id}`;
      const beforeLength = events.length;
      events = events.filter(e => e.id !== deleteId);
      if (events.length !== beforeLength) {
        cacheModified = true;
      }
    } else if (mutation.type === 'create' && mutation.event) {
      // Add new event to cache
      events.push(mutation.event);
      cacheModified = true;
    } else if (mutation.type === 'update' && mutation.event) {
      // Update existing event in cache
      events = events.map(e =>
        e.id === mutation.event.id ? mutation.event : e
      );
      cacheModified = true;
    }
  });

  if (cacheModified) {
    localStorage.setItem(CACHE_KEY, JSON.stringify(events));
    // Update timestamp so calendar knows cache was modified
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());

    // Dispatch custom event so calendar can update its React state
    window.dispatchEvent(new CustomEvent('calendarCacheUpdated', {
      detail: { mutations }
    }));
  }
};

const Chatbot = () => {
  const { getToken } = useAuth();
  const {
    isExpanded,
    setIsExpanded,
    isDetached,
    detachChatbot,
    dockChatbot,
    floatingPosition,
    setFloatingPosition,
    isDragging,
    setIsDragging,
    dragOffset,
    setDragOffset,
    isCalendarPage
  } = useChatbot();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { isManuallyCollapsed, toggleSidebar } = useSidebar();

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  // Handle drag start - detach chatbot from sidebar
  const handleDragStart = (e) => {
    e.preventDefault();

    const rect = containerRef.current.getBoundingClientRect();

    // Capture position and offset BEFORE detaching
    const initialPosition = { x: rect.left, y: rect.top };
    const offset = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };

    // Set all drag state in context (persists across component remount)
    setDragOffset(offset);
    setIsDragging(true);

    // Detach from sidebar when starting to drag (if not already detached)
    if (!isDetached) {
      setFloatingPosition(initialPosition);
      detachChatbot();
    }
  };

  // Handle Ocean logo click - dock when detached, collapse chatbot and sidebar when docked
  const handleLogoClick = () => {
    if (isDetached) {
      // If detached, dock back to sidebar
      dockChatbot();
      if (isManuallyCollapsed) {
        toggleSidebar(); // Expand the sidebar
      }
    } else {
      // If docked, collapse chatbot and also collapse the sidebar
      setIsExpanded(false);
      if (!isManuallyCollapsed) {
        toggleSidebar(); // Collapse the sidebar
      }
    }
  };

  // Handle dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      setFloatingPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (!isManuallyCollapsed) {
        toggleSidebar();
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset, setFloatingPosition, setIsDragging, isManuallyCollapsed, toggleSidebar]);

  // Auto-detach chatbot when sidebar collapses (only on calendar page)
  useEffect(() => {
    if (isCalendarPage && isManuallyCollapsed && !isDetached && isExpanded) {
      // Calculate current position before detaching
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setFloatingPosition({ x: rect.left, y: rect.top });
      }
      detachChatbot();
    }
  }, [isManuallyCollapsed, isDetached, isCalendarPage, isExpanded, detachChatbot, setFloatingPosition]);

  // Single fetch attempt against /api/chat. Throws an Error with isAuthError=true
  // when the Clerk token is missing or the backend rejects auth (401), so the
  // caller can retry once with a freshly-issued token.
  const attemptChatRequest = async (userMessage) => {
    const token = await getToken();
    if (!token) {
      const err = new Error("No auth token available");
      err.isAuthError = true;
      throw err;
    }

    const response = await fetch(`${config.API_BASE_URL}/api/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        message: userMessage,
        conversation_history: conversationHistory
      })
    });

    if (response.status === 401) {
      const err = new Error("Chat auth rejected by backend");
      err.isAuthError = true;
      throw err;
    }

    return { response, data: await response.json() };
  };

  const FRIENDLY_AUTH_ERROR =
    "I had trouble verifying your session. Please try sending that again in a moment.";
  const FRIENDLY_GENERIC_ERROR =
    "Sorry, something went wrong. Please try again.";

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      let result;
      try {
        result = await attemptChatRequest(userMessage);
      } catch (error) {
        if (!error.isAuthError) throw error;
        // Clerk may have been mid-refresh on the first call. Give the SDK a
        // beat to settle, then try once more with a freshly-issued token.
        console.warn("[Chatbot] First chat attempt failed auth, retrying with fresh token");
        await new Promise((resolve) => setTimeout(resolve, 300));
        result = await attemptChatRequest(userMessage);
      }

      const { response, data } = result;

      if (response.ok && data.status === "success") {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.data.response }
        ]);
        setConversationHistory(data.data.conversation_history);

        // Update localStorage cache if there were mutations
        if (data.data.mutations && data.data.mutations.length > 0) {
          console.log('[Chatbot] Received mutations:', data.data.mutations);
          updateLocalStorageCache(data.data.mutations);
        }
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: FRIENDLY_GENERIC_ERROR }
        ]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: error.isAuthError ? FRIENDLY_AUTH_ERROR : FRIENDLY_GENERIC_ERROR
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setConversationHistory([]);
  };

  // Collapsed state - just show the Ocean logo button (only when docked, not detached)
  if (!isExpanded && !isDetached && !isMobile) {
    return null;
  }

  // If mobile and expanded and on calendar page, render fullscreen overlay via Portal
  if (isMobile) {
    if ( isExpanded && isCalendarPage) {
      return createPortal(
        <div className={s.mobileOverlay}>
          <div className={s.mobileChatContainer}>
            <div className={`${s.chat} ${s.mobileChatContent}`}>
              <div className={s.header}>
                <span className={s.title}>Ocean</span>
                <div className={s.headerActions}>
                  <button
                    className={s.clearButton}
                    onClick={clearChat}
                    title="Clear chat"
                  >
                    Clear
                  </button>
                  <button
                    className={s.closeButton}
                    onClick={() => setIsExpanded(false)}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className={s.messages}>
                {messages.length === 0 && (
                  <div className={s.welcome}>
                    <p>Hi! I'm Ocean, your calendar assistant.</p>
                    <p className={s.hint}>
                      Ask me about your schedule, upcoming exams, or create new events.
                    </p>
                  </div>
                )}
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`${s.message} ${s[msg.role]}`}
                  >
                    {msg.role === "assistant" ? (
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className={`${s.message} ${s.assistant} ${s.loading}`}>
                    <span className={s.dot}></span>
                    <span className={s.dot}></span>
                    <span className={s.dot}></span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className={s.inputArea}>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Message Ocean..."
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className={s.sendButton}
                >
                  ↑
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      );
    }
    return null;
  }

  // Chat window element
  const chatWindow = (
    <div
      ref={containerRef}
      className={`${s.chat} ${isDetached ? s.floating : ''}`}
      style={isDetached ? { position: "fixed", left: floatingPosition.x, top: floatingPosition.y } : {}}
    >
      <div className={s.header}>
        <button
          className={s.logoButton}
          onClick={handleLogoClick}
          title={isDetached ? "Dock to sidebar" : "Minimize"}
        >
          <img src={clearLogo} alt="Ocean" />
        </button>
        <span className={s.title}>Ocean</span>
        <div className={s.headerActions}>
          <button
            className={s.clearButton}
            onClick={clearChat}
            title="Clear chat"
          >
            Clear
          </button>
          <div
            className={s.dragHandle}
            onMouseDown={handleDragStart}
            title="Drag to move"
          >
            ☰
          </div>
        </div>
      </div>

      <div className={s.messages}>
        {messages.length === 0 && (
          <div className={s.welcome}>
            <p>Hi! I'm Ocean, your calendar assistant.</p>
            <p className={s.hint}>
              Ask me about your schedule, upcoming exams, or create new events.
            </p>
          </div>
        )}
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`${s.message} ${s[msg.role]}`}
          >
            {msg.role === "assistant" ? (
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            ) : (
              msg.content
            )}
          </div>
        ))}
        {isLoading && (
          <div className={`${s.message} ${s.assistant} ${s.loading}`}>
            <span className={s.dot}></span>
            <span className={s.dot}></span>
            <span className={s.dot}></span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={s.inputArea}>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message Ocean..."
          disabled={isLoading}
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || isLoading}
          className={s.sendButton}
        >
          ↑
        </button>
      </div>
    </div>
  );

  // If detached, render via portal to body (outside RightPanel)
  if (isDetached) {
    return createPortal(chatWindow, document.body);
  }

  // Otherwise render normally in RightPanel
  return chatWindow;
};

export default Chatbot;
